import {AA_SEQUENCE_EVENT_PAYLOADS} from 'constants/events/AaSequenceEventPayloads';
import {ARCHIVE_EVENT_PAYLOADS} from 'constants/events/ArchiveEventPayloads';
import {BULK_WORKFLOWS_EVENT_PAYLOADS} from 'constants/events/BulkWorkflowsEventPayloads';
import {CALENDAR_EVENT_PAYLOADS} from 'constants/events/CalendarEventPayloads';
import {CONTAINABLE_LISTING_EVENT_PAYLOADS} from 'constants/events/ContainableListingEventPayloads';
import {CRISPR_EVENT_PAYLOADS} from 'constants/events/CRISPREventPayloads';
import {DOCUMENT_EVENT_PAYLOADS} from 'constants/events/DocumentEventPayloads';
import {ENTRY_EVENT_PAYLOADS} from 'constants/events/EntryEventPayloads';
import type {UntypedPayload} from 'constants/events/EventPayloadUtils';
import {EXPORT_EVENT_PAYLOADS} from 'constants/events/ExportEventPayloads';
import {FILE_BROWSER_EVENT_PAYLOADS} from 'constants/events/FileBrowserEventPayloads';
import {INVENTORY_EVENT_PAYLOADS} from 'constants/events/InventoryEventPayloads';
import {LINK_EVENT_PAYLOADS} from 'constants/events/LinkEventPayloads';
import {MODEL_HUB_EVENT_PAYLOADS} from 'constants/events/ModelHubEventPayloads';
import {NAV_EVENT_PAYLOADS} from 'constants/events/NavEventPayloads';
import {NOTE_EVENT_PAYLOADS} from 'constants/events/NoteEventPayloads';
import {NOTIFICATIONS_EVENT_PAYLOADS} from 'constants/events/NotificationsEventPayloads';
import {ONBOARDING_EVENT_PAYLOADS} from 'constants/events/OnboardingEventPayloads';
import {ORDER_EVENT_PAYLOADS} from 'constants/events/OrderEventPayloads';
import {ORGANIZATION_MEMBER_EVENT_PAYLOADS} from 'constants/events/OrganizationMemberEventPayloads';
import {ORG_EVENT_PAYLOADS} from 'constants/events/OrgEventPayloads';
import {PROCESS_MANAGEMENT_EVENT_PAYLOADS} from 'constants/events/ProcessManagementEventPayloads';
import {PROTOCOL_EVENT_PAYLOADS} from 'constants/events/ProtocolEventPayloads';
import {REGISTRY_EVENT_PAYLOADS} from 'constants/events/RegistryEventPayloads';
import {SCHEMA_EVENT_PAYLOADS} from 'constants/events/SchemaEventPayloads';
import {SPREADSHEET_BULK_IMPORT_EVENT_PAYLOADS} from 'constants/events/SpreadsheetBulkImportEventPayloads';
import {STRUCTURED_TABLE_EVENT_PAYLOADS} from 'constants/events/StructuredTableEventPayloads';
import {SUBTEMPLATE_EVENT_PAYLOADS} from 'constants/events/SubtemplateEventPayloads';
import {TEMPLATE_COLLECTION_EVENT_PAYLOADS} from 'constants/events/TemplateCollectionEventPayloads';
import {TEMPLATE_EVENT_PAYLOADS} from 'constants/events/TemplateEventPayloads';
import {TFI_EVENT_PAYLOADS} from 'constants/events/TFIEventPayloads';
import {WORKFLOW_EVENT_PAYLOADS} from 'constants/events/WorkflowEventPayloads';

// Re-export DocumentUpdateSpec for backward compatibility with existing import sites.
export type {DocumentUpdateSpec} from 'constants/events/DocumentEventPayloads';

/**
 * A map of all possible event names and their payload types. This constant should not be exported or used
 * directly outside this file.
 *
 * We use an object to declare the payloads as it allows us to easily enforce at compile time that each event
 * name is unique and only defined in one place.
 *
 * We then derive our EventConstants (as a string literal enum) and FluxAction (as a discriminated union type)
 * from it.
 */
const EVENT_PAYLOADS = {
  HOME_DASHBOARD_FETCH_ENTRY_REVIEW_SUMMARY: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_ENTRY_REVIEW_SUMMARY_COMPLETED: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_ENTRY_REVIEW_SUMMARY_FAILED: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_WORKSHEET_REVIEW_SUMMARY: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_WORKSHEET_REVIEW_SUMMARY_COMPLETED: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_WORKSHEET_REVIEW_SUMMARY_FAILED: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_TEMPLATE_REVIEW_SUMMARY: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_TEMPLATE_REVIEW_SUMMARY_COMPLETED: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_TEMPLATE_REVIEW_SUMMARY_FAILED: {} as UntypedPayload,
  ONBOARDING_ACKNOWLEDGE_COMPLETED_MODULE: {} as UntypedPayload,
  ONBOARDING_COMPLETE_TASK: {} as UntypedPayload,
  ONBOARDING_DISMISS_NUE_MODULE: {} as UntypedPayload,
  ONBOARDING_FETCH_GETTING_STARTED_TASKS_COMPLETED: {} as UntypedPayload,
  ONBOARDING_OPEN_NUE_MODULE_INDEX: {} as UntypedPayload,
  ONBOARDING_RESTORE_NUE_MODULE: {} as UntypedPayload,
  ONBOARDING_TOGGLE_COLLAPSED_COMPLETED_NUE_MODULE: {} as UntypedPayload,
  ORG_ACCEPT_REQUEST: {} as UntypedPayload,
  ORG_REJECT_REQUEST: {} as UntypedPayload,
  USER_ACCEPT_ORG_REQUEST: {} as UntypedPayload,
  USER_DISMISS_ALL_ORG_INVITES: {} as UntypedPayload,
  USER_DISMISS_ALL_ORG_INVITES_FAILED: {} as UntypedPayload,
  USER_FETCH_ORG_REQUESTS_COMPLETED: {} as UntypedPayload,
  USER_REJECT_ORG_REQUEST: {} as UntypedPayload,

  // ── Family modules ────────────────────────────────────────────────────────────
  ...ORGANIZATION_MEMBER_EVENT_PAYLOADS,
  ...CONTAINABLE_LISTING_EVENT_PAYLOADS,
  ...EXPORT_EVENT_PAYLOADS,
  ...NOTE_EVENT_PAYLOADS,
  ...DOCUMENT_EVENT_PAYLOADS,
  ...ENTRY_EVENT_PAYLOADS,
  ...STRUCTURED_TABLE_EVENT_PAYLOADS,
  ...SUBTEMPLATE_EVENT_PAYLOADS,
  ...TEMPLATE_EVENT_PAYLOADS,
  ...TEMPLATE_COLLECTION_EVENT_PAYLOADS,
  ...PROTOCOL_EVENT_PAYLOADS,
  ...AA_SEQUENCE_EVENT_PAYLOADS,
  ...ORG_EVENT_PAYLOADS,
  ...ORDER_EVENT_PAYLOADS,
  ...NAV_EVENT_PAYLOADS,
  ...FILE_BROWSER_EVENT_PAYLOADS,
  ...NOTIFICATIONS_EVENT_PAYLOADS,
  ...LINK_EVENT_PAYLOADS,
  ...MODEL_HUB_EVENT_PAYLOADS,
  ...CALENDAR_EVENT_PAYLOADS,
  ...ONBOARDING_EVENT_PAYLOADS,
  ...CRISPR_EVENT_PAYLOADS,
  ...BULK_WORKFLOWS_EVENT_PAYLOADS,
  ...ARCHIVE_EVENT_PAYLOADS,
  ...WORKFLOW_EVENT_PAYLOADS,
  ...SPREADSHEET_BULK_IMPORT_EVENT_PAYLOADS,
  ...TFI_EVENT_PAYLOADS,
  ...INVENTORY_EVENT_PAYLOADS,
  ...REGISTRY_EVENT_PAYLOADS,
  ...SCHEMA_EVENT_PAYLOADS,
  ...PROCESS_MANAGEMENT_EVENT_PAYLOADS,
} as const;

/**
 * A string literal enum of all possible event names.
 */
export const EventConstants = Object.keys(EVENT_PAYLOADS).reduce<Record<string, string>>((acc, key) => {
  acc[key] = key;
  return acc;
}, {}) as {[K in keyof typeof EVENT_PAYLOADS]: K};

type EventNames = keyof typeof EVENT_PAYLOADS;

/**
 * A discriminated union of all possible event payload types.
 */
export type FluxAction = {
  [EventName in EventNames]: {actionType: EventName} & (typeof EVENT_PAYLOADS)[EventName];
}[EventNames];
